import { useEffect, useEffectEvent, useRef } from 'react';
import DynamicFormOutlined from '@mui/icons-material/DynamicFormOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import type { ApiError } from '../api/httpClientFactory';
import { COPY } from '../copy';
import { toSearch, withParams } from './catalogParams';
import { kindLabel } from './labels';
import type { useFormSearch } from './useFormSearch';

interface FormListProps {
  search: ReturnType<typeof useFormSearch>;
  selectedId: number | null;
  q: string;
  categoryId: number | null;
  scopeName: string;
  onClearSearch: () => void;
  onShowAll: () => void;
}

export function FormList({
  search,
  selectedId,
  q,
  categoryId,
  scopeName,
  onClearSearch,
  onShowAll,
}: FormListProps) {
  const [sp] = useSearchParams();
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { status, items, nextCursor, error, loadingMore, moreError, loadMore, retry } = search;
  const query = q.trim();

  const onReachSentinel = useEffectEvent(() => {
    loadMore();
  });

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || nextCursor === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) onReachSentinel();
      },
      { rootMargin: '200px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [nextCursor]);

  if (status === 'loading') {
    return <LinearProgress aria-label={COPY.loadingForms} />;
  }

  if (status === 'error' && error) {
    return (
      <Alert
        severity="error"
        action={
          <Button onClick={retry} color="inherit">
            {COPY.tryAgain}
          </Button>
        }
        sx={{ m: 2 }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {COPY.formsErrorTitle}
        </Typography>
        <Typography variant="body2">{reference(error)}</Typography>
      </Alert>
    );
  }

  if (status !== 'ready') return null;

  return (
    <>
      <Typography variant="body2" color="text.secondary" role="status" sx={{ px: 2, py: 1 }}>
        {COPY.scopeLine(scopeName, query, items.length, nextCursor !== null)}
      </Typography>
      {items.length === 0 ? (
        <EmptyForms
          q={query}
          categoryId={categoryId}
          onClearSearch={onClearSearch}
          onShowAll={onShowAll}
        />
      ) : (
        <List aria-label={COPY.formsListLabel} disablePadding>
          {items.map((form) => (
            <ListItem key={form.id} disablePadding>
              <ListItemButton
                component={RouterLink}
                to={{ search: toSearch(withParams(sp, { form: form.id, tab: null, page: null })) }}
                selected={selectedId === form.id}
                aria-current={selectedId === form.id ? 'true' : undefined}
                divider
                nativeButton={false}
                sx={{
                  alignItems: 'flex-start',
                  gap: 1.5,
                  '&.Mui-selected .MuiListItemText-primary': { fontWeight: 700 },
                }}
              >
                <ListItemIcon sx={{ minWidth: 0, mt: 0.25 }}>
                  {form.kind === 'XDP' ? (
                    <PictureAsPdfOutlined aria-hidden />
                  ) : (
                    <DynamicFormOutlined aria-hidden />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={form.title}
                  secondary={`${form.code} · ${kindLabel(form.kind)}`}
                  slotProps={{
                    primary: { variant: 'body2', sx: { fontWeight: 600 } },
                    secondary: { variant: 'body2' },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      {nextCursor !== null && (
        <>
          <Box ref={sentinelRef} />
          <Box sx={{ px: 2 }}>
            <Button
              variant="outlined"
              fullWidth
              loading={loadingMore}
              onClick={loadMore}
              sx={{ my: 2 }}
            >
              {COPY.showMore}
            </Button>
          </Box>
        </>
      )}
      {moreError && (
        <Alert
          severity="error"
          action={
            <Button onClick={loadMore} color="inherit">
              {COPY.tryAgain}
            </Button>
          }
          sx={{ m: 2 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {COPY.moreErrorTitle}
          </Typography>
          <Typography variant="body2">{reference(moreError)}</Typography>
        </Alert>
      )}
    </>
  );
}

function reference(error: ApiError): string {
  return COPY.reference(error.requestId ?? error.code);
}

function EmptyForms({
  q,
  categoryId,
  onClearSearch,
  onShowAll,
}: {
  q: string;
  categoryId: number | null;
  onClearSearch: () => void;
  onShowAll: () => void;
}) {
  if (q !== '') {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {COPY.noMatchTitle(q)}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {COPY.noMatchBody}
        </Typography>
        <Button variant="outlined" onClick={onClearSearch}>
          {COPY.clearSearch}
        </Button>
      </Box>
    );
  }
  if (categoryId !== null) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {COPY.emptyCategoryTitle}
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {COPY.emptyCategoryBody}
        </Typography>
        <Button variant="outlined" onClick={onShowAll}>
          {COPY.showAllForms}
        </Button>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body1" sx={{ fontWeight: 600 }}>
        {COPY.noFormsTitle}
      </Typography>
      <Typography variant="body2">{COPY.noFormsBody}</Typography>
    </Box>
  );
}
